/**
 * Reels Project Store
 * State management for the video editor using Zustand-like pattern
 * For CloudMortgage.io - Ultra membership only
 */

import { createContext, useContext, useReducer, useCallback } from 'react';

// Initial project state
const initialProject = {
  id: null,
  name: 'Untitled Reel',
  createdAt: null,
  modifiedAt: null,
  settings: {
    width: 1080,
    height: 1920, // 9:16 aspect ratio for Reels
    frameRate: 30,
    duration: 0,
  },
  mediaLibrary: [],
  tracks: [
    { id: 'video-1', type: 'video', name: 'Video 1', clips: [], muted: false, locked: false, visible: true },
    { id: 'audio-1', type: 'audio', name: 'Audio 1', clips: [], muted: false, locked: false, volume: 1 },
  ],
  textClips: [],
  shapeClips: [],
  stickerClips: [],
};

// Initial UI state
const initialUIState = {
  selectedClipId: null,
  selectedTrackId: null,
  selectedTextId: null,
  isPlaying: false,
  currentTime: 0,
  zoom: 1,
  snapEnabled: true,
  activePanel: 'media', // media, text, effects, audio, export
  isDragging: false,
  isScrubbing: false,
};

// Action types
const ActionTypes = {
  // Project actions
  NEW_PROJECT: 'NEW_PROJECT',
  LOAD_PROJECT: 'LOAD_PROJECT',
  UPDATE_PROJECT_NAME: 'UPDATE_PROJECT_NAME',
  UPDATE_SETTINGS: 'UPDATE_SETTINGS',

  // Media library
  ADD_MEDIA: 'ADD_MEDIA',
  REMOVE_MEDIA: 'REMOVE_MEDIA',

  // Track actions
  ADD_TRACK: 'ADD_TRACK',
  REMOVE_TRACK: 'REMOVE_TRACK',
  UPDATE_TRACK: 'UPDATE_TRACK',
  REORDER_TRACKS: 'REORDER_TRACKS',

  // Clip actions
  ADD_CLIP: 'ADD_CLIP',
  REMOVE_CLIP: 'REMOVE_CLIP',
  UPDATE_CLIP: 'UPDATE_CLIP',
  MOVE_CLIP: 'MOVE_CLIP',
  TRIM_CLIP: 'TRIM_CLIP',
  SPLIT_CLIP: 'SPLIT_CLIP',

  // Text clip actions
  ADD_TEXT_CLIP: 'ADD_TEXT_CLIP',
  UPDATE_TEXT_CLIP: 'UPDATE_TEXT_CLIP',
  REMOVE_TEXT_CLIP: 'REMOVE_TEXT_CLIP',

  // Shape/Sticker actions
  ADD_SHAPE_CLIP: 'ADD_SHAPE_CLIP',
  ADD_STICKER_CLIP: 'ADD_STICKER_CLIP',
  UPDATE_SHAPE_CLIP: 'UPDATE_SHAPE_CLIP',
  REMOVE_OVERLAY_CLIP: 'REMOVE_OVERLAY_CLIP',

  // Effect actions
  ADD_EFFECT: 'ADD_EFFECT',
  UPDATE_EFFECT: 'UPDATE_EFFECT',
  REMOVE_EFFECT: 'REMOVE_EFFECT',

  // UI actions
  SET_SELECTED_CLIP: 'SET_SELECTED_CLIP',
  SET_SELECTED_TRACK: 'SET_SELECTED_TRACK',
  SET_SELECTED_TEXT: 'SET_SELECTED_TEXT',
  SET_PLAYING: 'SET_PLAYING',
  SET_CURRENT_TIME: 'SET_CURRENT_TIME',
  SET_ZOOM: 'SET_ZOOM',
  SET_SNAP_ENABLED: 'SET_SNAP_ENABLED',
  SET_ACTIVE_PANEL: 'SET_ACTIVE_PANEL',
  SET_DRAGGING: 'SET_DRAGGING',
  SET_SCRUBBING: 'SET_SCRUBBING',
  CLEAR_SELECTION: 'CLEAR_SELECTION',
};

// Project reducer
function projectReducer(state, action) {
  switch (action.type) {
    case ActionTypes.NEW_PROJECT:
      return {
        ...initialProject,
        id: `reel-${Date.now()}`,
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.LOAD_PROJECT:
      return { ...action.payload, modifiedAt: new Date().toISOString() };

    case ActionTypes.UPDATE_PROJECT_NAME:
      return { ...state, name: action.payload, modifiedAt: new Date().toISOString() };

    case ActionTypes.UPDATE_SETTINGS:
      return { ...state, settings: { ...state.settings, ...action.payload }, modifiedAt: new Date().toISOString() };

    case ActionTypes.ADD_MEDIA:
      return {
        ...state,
        mediaLibrary: [...state.mediaLibrary, action.payload],
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.REMOVE_MEDIA:
      return {
        ...state,
        mediaLibrary: state.mediaLibrary.filter(m => m.id !== action.payload),
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.ADD_TRACK:
      return {
        ...state,
        tracks: [...state.tracks, action.payload],
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.REMOVE_TRACK:
      return {
        ...state,
        tracks: state.tracks.filter(t => t.id !== action.payload),
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.UPDATE_TRACK:
      return {
        ...state,
        tracks: state.tracks.map(t => t.id === action.payload.id ? { ...t, ...action.payload.updates } : t),
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.ADD_CLIP: {
      const { trackId, clip } = action.payload;
      return {
        ...state,
        tracks: state.tracks.map(t =>
          t.id === trackId ? { ...t, clips: [...t.clips, clip] } : t
        ),
        modifiedAt: new Date().toISOString(),
      };
    }

    case ActionTypes.REMOVE_CLIP: {
      const { trackId, clipId } = action.payload;
      return {
        ...state,
        tracks: state.tracks.map(t =>
          t.id === trackId ? { ...t, clips: t.clips.filter(c => c.id !== clipId) } : t
        ),
        modifiedAt: new Date().toISOString(),
      };
    }

    case ActionTypes.UPDATE_CLIP: {
      const { trackId, clipId, updates } = action.payload;
      return {
        ...state,
        tracks: state.tracks.map(t =>
          t.id === trackId
            ? { ...t, clips: t.clips.map(c => c.id === clipId ? { ...c, ...updates } : c) }
            : t
        ),
        modifiedAt: new Date().toISOString(),
      };
    }

    case ActionTypes.MOVE_CLIP: {
      const { fromTrackId, toTrackId, clipId, newStartTime } = action.payload;
      let movedClip = null;

      const tracksWithoutClip = state.tracks.map(t => {
        if (t.id === fromTrackId) {
          const clip = t.clips.find(c => c.id === clipId);
          if (clip) {
            movedClip = { ...clip, startTime: newStartTime };
          }
          return { ...t, clips: t.clips.filter(c => c.id !== clipId) };
        }
        return t;
      });

      if (!movedClip) return state;

      return {
        ...state,
        tracks: tracksWithoutClip.map(t =>
          t.id === toTrackId ? { ...t, clips: [...t.clips, movedClip] } : t
        ),
        modifiedAt: new Date().toISOString(),
      };
    }

    case ActionTypes.SPLIT_CLIP: {
      const { trackId, clipId, splitTime } = action.payload;
      return {
        ...state,
        tracks: state.tracks.map(t => {
          if (t.id !== trackId) return t;
          const clip = t.clips.find(c => c.id === clipId);
          if (!clip) return t;

          const relativeTime = splitTime - clip.startTime;
          const firstClip = {
            ...clip,
            id: `${clip.id}-a`,
            duration: relativeTime,
            outPoint: clip.inPoint + relativeTime,
          };
          const secondClip = {
            ...clip,
            id: `${clip.id}-b`,
            startTime: splitTime,
            duration: clip.duration - relativeTime,
            inPoint: clip.inPoint + relativeTime,
          };

          return {
            ...t,
            clips: [...t.clips.filter(c => c.id !== clipId), firstClip, secondClip],
          };
        }),
        modifiedAt: new Date().toISOString(),
      };
    }

    case ActionTypes.ADD_TEXT_CLIP:
      return {
        ...state,
        textClips: [...state.textClips, action.payload],
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.UPDATE_TEXT_CLIP:
      return {
        ...state,
        textClips: state.textClips.map(t =>
          t.id === action.payload.id ? { ...t, ...action.payload.updates } : t
        ),
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.REMOVE_TEXT_CLIP:
      return {
        ...state,
        textClips: state.textClips.filter(t => t.id !== action.payload),
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.ADD_SHAPE_CLIP:
      return {
        ...state,
        shapeClips: [...state.shapeClips, action.payload],
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.ADD_STICKER_CLIP:
      return {
        ...state,
        stickerClips: [...state.stickerClips, action.payload],
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.REMOVE_OVERLAY_CLIP:
      return {
        ...state,
        textClips: state.textClips.filter(t => t.id !== action.payload),
        shapeClips: state.shapeClips.filter(s => s.id !== action.payload),
        stickerClips: state.stickerClips.filter(s => s.id !== action.payload),
        modifiedAt: new Date().toISOString(),
      };

    case ActionTypes.ADD_EFFECT: {
      const { clipId, trackId, effect } = action.payload;
      return {
        ...state,
        tracks: state.tracks.map(t =>
          t.id === trackId
            ? {
                ...t,
                clips: t.clips.map(c =>
                  c.id === clipId ? { ...c, effects: [...(c.effects || []), effect] } : c
                ),
              }
            : t
        ),
        modifiedAt: new Date().toISOString(),
      };
    }

    case ActionTypes.UPDATE_EFFECT: {
      const { clipId, trackId, effectId, updates } = action.payload;
      return {
        ...state,
        tracks: state.tracks.map(t =>
          t.id === trackId
            ? {
                ...t,
                clips: t.clips.map(c =>
                  c.id === clipId
                    ? {
                        ...c,
                        effects: (c.effects || []).map(e =>
                          e.id === effectId ? { ...e, ...updates } : e
                        ),
                      }
                    : c
                ),
              }
            : t
        ),
        modifiedAt: new Date().toISOString(),
      };
    }

    case ActionTypes.REMOVE_EFFECT: {
      const { clipId, trackId, effectId } = action.payload;
      return {
        ...state,
        tracks: state.tracks.map(t =>
          t.id === trackId
            ? {
                ...t,
                clips: t.clips.map(c =>
                  c.id === clipId
                    ? { ...c, effects: (c.effects || []).filter(e => e.id !== effectId) }
                    : c
                ),
              }
            : t
        ),
        modifiedAt: new Date().toISOString(),
      };
    }

    default:
      return state;
  }
}

// UI reducer
function uiReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_SELECTED_CLIP:
      return { ...state, selectedClipId: action.payload, selectedTextId: null };
    case ActionTypes.SET_SELECTED_TRACK:
      return { ...state, selectedTrackId: action.payload };
    case ActionTypes.SET_SELECTED_TEXT:
      return { ...state, selectedTextId: action.payload, selectedClipId: null };
    case ActionTypes.SET_PLAYING:
      return { ...state, isPlaying: action.payload };
    case ActionTypes.SET_CURRENT_TIME:
      return { ...state, currentTime: action.payload };
    case ActionTypes.SET_ZOOM:
      return { ...state, zoom: action.payload };
    case ActionTypes.SET_SNAP_ENABLED:
      return { ...state, snapEnabled: action.payload };
    case ActionTypes.SET_ACTIVE_PANEL:
      return { ...state, activePanel: action.payload };
    case ActionTypes.SET_DRAGGING:
      return { ...state, isDragging: action.payload };
    case ActionTypes.SET_SCRUBBING:
      return { ...state, isScrubbing: action.payload };
    case ActionTypes.CLEAR_SELECTION:
      return { ...state, selectedClipId: null, selectedTrackId: null, selectedTextId: null };
    default:
      return state;
  }
}

// Create context
const ProjectContext = createContext(null);
const UIContext = createContext(null);

// Provider component
export function ReelsProjectProvider({ children }) {
  const [project, dispatchProject] = useReducer(projectReducer, initialProject);
  const [ui, dispatchUI] = useReducer(uiReducer, initialUIState);

  // Project actions
  const newProject = useCallback(() => {
    dispatchProject({ type: ActionTypes.NEW_PROJECT });
  }, []);

  const loadProject = useCallback((projectData) => {
    dispatchProject({ type: ActionTypes.LOAD_PROJECT, payload: projectData });
  }, []);

  const updateProjectName = useCallback((name) => {
    dispatchProject({ type: ActionTypes.UPDATE_PROJECT_NAME, payload: name });
  }, []);

  const updateSettings = useCallback((settings) => {
    dispatchProject({ type: ActionTypes.UPDATE_SETTINGS, payload: settings });
  }, []);

  const addMedia = useCallback((media) => {
    dispatchProject({ type: ActionTypes.ADD_MEDIA, payload: media });
  }, []);

  const removeMedia = useCallback((mediaId) => {
    dispatchProject({ type: ActionTypes.REMOVE_MEDIA, payload: mediaId });
  }, []);

  const addTrack = useCallback((track) => {
    dispatchProject({ type: ActionTypes.ADD_TRACK, payload: track });
  }, []);

  const removeTrack = useCallback((trackId) => {
    dispatchProject({ type: ActionTypes.REMOVE_TRACK, payload: trackId });
  }, []);

  const updateTrack = useCallback((id, updates) => {
    dispatchProject({ type: ActionTypes.UPDATE_TRACK, payload: { id, updates } });
  }, []);

  const addClip = useCallback((trackId, clip) => {
    dispatchProject({ type: ActionTypes.ADD_CLIP, payload: { trackId, clip } });
  }, []);

  const removeClip = useCallback((trackId, clipId) => {
    dispatchProject({ type: ActionTypes.REMOVE_CLIP, payload: { trackId, clipId } });
  }, []);

  const updateClip = useCallback((trackId, clipId, updates) => {
    dispatchProject({ type: ActionTypes.UPDATE_CLIP, payload: { trackId, clipId, updates } });
  }, []);

  const moveClip = useCallback((fromTrackId, toTrackId, clipId, newStartTime) => {
    dispatchProject({ type: ActionTypes.MOVE_CLIP, payload: { fromTrackId, toTrackId, clipId, newStartTime } });
  }, []);

  const splitClip = useCallback((trackId, clipId, splitTime) => {
    dispatchProject({ type: ActionTypes.SPLIT_CLIP, payload: { trackId, clipId, splitTime } });
  }, []);

  const addTextClip = useCallback((textClip) => {
    dispatchProject({ type: ActionTypes.ADD_TEXT_CLIP, payload: textClip });
  }, []);

  const updateTextClip = useCallback((id, updates) => {
    dispatchProject({ type: ActionTypes.UPDATE_TEXT_CLIP, payload: { id, updates } });
  }, []);

  const removeTextClip = useCallback((id) => {
    dispatchProject({ type: ActionTypes.REMOVE_TEXT_CLIP, payload: id });
  }, []);

  const addShapeClip = useCallback((shapeClip) => {
    dispatchProject({ type: ActionTypes.ADD_SHAPE_CLIP, payload: shapeClip });
  }, []);

  const addStickerClip = useCallback((stickerClip) => {
    dispatchProject({ type: ActionTypes.ADD_STICKER_CLIP, payload: stickerClip });
  }, []);

  const removeOverlayClip = useCallback((id) => {
    dispatchProject({ type: ActionTypes.REMOVE_OVERLAY_CLIP, payload: id });
  }, []);

  const addEffect = useCallback((trackId, clipId, effect) => {
    dispatchProject({ type: ActionTypes.ADD_EFFECT, payload: { trackId, clipId, effect } });
  }, []);

  const updateEffect = useCallback((trackId, clipId, effectId, updates) => {
    dispatchProject({ type: ActionTypes.UPDATE_EFFECT, payload: { trackId, clipId, effectId, updates } });
  }, []);

  const removeEffect = useCallback((trackId, clipId, effectId) => {
    dispatchProject({ type: ActionTypes.REMOVE_EFFECT, payload: { trackId, clipId, effectId } });
  }, []);

  // UI actions
  const setSelectedClip = useCallback((clipId) => {
    dispatchUI({ type: ActionTypes.SET_SELECTED_CLIP, payload: clipId });
  }, []);

  const setSelectedTrack = useCallback((trackId) => {
    dispatchUI({ type: ActionTypes.SET_SELECTED_TRACK, payload: trackId });
  }, []);

  const setSelectedText = useCallback((textId) => {
    dispatchUI({ type: ActionTypes.SET_SELECTED_TEXT, payload: textId });
  }, []);

  const setPlaying = useCallback((isPlaying) => {
    dispatchUI({ type: ActionTypes.SET_PLAYING, payload: isPlaying });
  }, []);

  const setCurrentTime = useCallback((time) => {
    dispatchUI({ type: ActionTypes.SET_CURRENT_TIME, payload: time });
  }, []);

  const setZoom = useCallback((zoom) => {
    dispatchUI({ type: ActionTypes.SET_ZOOM, payload: zoom });
  }, []);

  const setSnapEnabled = useCallback((enabled) => {
    dispatchUI({ type: ActionTypes.SET_SNAP_ENABLED, payload: enabled });
  }, []);

  const setActivePanel = useCallback((panel) => {
    dispatchUI({ type: ActionTypes.SET_ACTIVE_PANEL, payload: panel });
  }, []);

  const setDragging = useCallback((isDragging) => {
    dispatchUI({ type: ActionTypes.SET_DRAGGING, payload: isDragging });
  }, []);

  const setScrubbing = useCallback((isScrubbing) => {
    dispatchUI({ type: ActionTypes.SET_SCRUBBING, payload: isScrubbing });
  }, []);

  const clearSelection = useCallback(() => {
    dispatchUI({ type: ActionTypes.CLEAR_SELECTION });
  }, []);

  const projectValue = {
    project,
    actions: {
      newProject,
      loadProject,
      updateProjectName,
      updateSettings,
      addMedia,
      removeMedia,
      addTrack,
      removeTrack,
      updateTrack,
      addClip,
      removeClip,
      updateClip,
      moveClip,
      splitClip,
      addTextClip,
      updateTextClip,
      removeTextClip,
      addShapeClip,
      addStickerClip,
      removeOverlayClip,
      addEffect,
      updateEffect,
      removeEffect,
    },
  };

  const uiValue = {
    ui,
    actions: {
      setSelectedClip,
      setSelectedTrack,
      setSelectedText,
      setPlaying,
      setCurrentTime,
      setZoom,
      setSnapEnabled,
      setActivePanel,
      setDragging,
      setScrubbing,
      clearSelection,
    },
  };

  return (
    <ProjectContext.Provider value={projectValue}>
      <UIContext.Provider value={uiValue}>
        {children}
      </UIContext.Provider>
    </ProjectContext.Provider>
  );
}

// Hooks
export function useProject() {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ReelsProjectProvider');
  }
  return context;
}

export function useUI() {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a ReelsProjectProvider');
  }
  return context;
}

// Helper to get total duration
export function getProjectDuration(project) {
  let maxTime = 0;
  project.tracks.forEach(track => {
    track.clips.forEach(clip => {
      const endTime = clip.startTime + clip.duration;
      if (endTime > maxTime) maxTime = endTime;
    });
  });
  project.textClips.forEach(clip => {
    const endTime = clip.startTime + clip.duration;
    if (endTime > maxTime) maxTime = endTime;
  });
  return maxTime;
}
